/** @flow */

import { takeLatest, put, all, call } from "redux-saga/effects";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import type { Action } from "redux-action";
import {
  ExportFocusListsManagerReport,
  ExportFocusListsManagerReportSuccess,
  GetReportingFocusListsManagerReport,
  GetFocusListsManagerReportSchema,
  CreateFocusListsManagerSavedReport,
  UpdateFocusListsManagerSavedReport,
  DeleteFocusListsManagerSavedReport,
} from "@modules/reporting/constants/actions";
import actionCreator from "@modules/reporting/actionCreator";
import type { ManagerReport } from "@modules/reporting/reducer/focus-lists/manager/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import actionCreators from "@modules/reporting/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";

type ReportDataRequest = $Exact<{
  clientId: number,
  report: ManagerReport,
  canSave?: boolean,
}>;

export function* onExportFocusListsManagerReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/manager/export`,
      { filters: payload.report.filters, columns: payload.report.columns },
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportFocusListsManagerReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportFocusListsManagerReportError(e.response));
  }
}

export function* onExportFocusListsManagerReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "manager-focus-lists-report.csv");
}

function* onGetReportingFocusListsManagerReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/manager`,
      payload.report.filters,
      {
        timeout: 300000,
      },
    );

    yield all([
      put(
        actionCreator.GetReportingFocusListsManagerReportSuccess({
          details: payload.report,
          reportData: response.data.content,
        }),
      ),
      put(push(route("REPORTING_FOCUS_LISTS_MANAGER_REPORT", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(
      actionCreator.GetReportingFocusListsManagerReportError({
        error: e.message,
      }),
    );
  }
}

type ReportSchemaRequest = $Exact<{
  clientId: number,
}>;

export function* onGetFocusListsManagerReportSchema({
  payload,
}: Action<ReportSchemaRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const schema = yield parseSchema("ManagerFocusListReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetFocusListsManagerReportSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetFocusListsManagerReportSchemaError(e.response));
  }
}

type CreateFocusListsManagerSavedReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  columns: string[],
  name: string,
}>;

export function* onCreateFocusListsManagerSavedReport({
  payload,
}: Action<CreateFocusListsManagerSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.columns || !payload.name) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/manager/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
        columns: payload.columns,
      },
    );

    yield all([
      put(actionCreators.CreateFocusListsManagerSavedReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingFocusListsManagerMyReports({ clientId: payload.clientId })),
      put(
        actionCreators.GetReportingFocusListsManagerReport({
          clientId: payload.clientId,
          report: payload,
        }),
      ),
      put(actionCreators.SetFocusListsManagerReportToSave(response.data)),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateFocusListsManagerSavedReportError(e.response));
  }
}

type UpdateFocusListsManagerSavedReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateFocusListsManagerSavedReport({
  payload,
}: Action<UpdateFocusListsManagerSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/manager/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetReportingFocusListsManagerReport({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [
          put(
            actionCreators.GetReportingFocusListsManagerMyReports({
              clientId: payload.clientId,
            }),
          ),
        ];

    yield all([
      put(actionCreators.UpdateFocusListsManagerSavedReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateFocusListsManagerSavedReportError(e.response));
  }
}

type DeleteFocusListsManagerSavedReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteFocusListsManagerSavedReport({
  payload,
}: Action<DeleteFocusListsManagerSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.delete,
      `/api/v1/focus-lists/client/${payload.clientId}/focus-lists/manager/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteFocusListsManagerSavedReportSuccess()),
      put(actionCreators.GetReportingFocusListsManagerMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteFocusListsManagerSavedReportError(e.response));
  }
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeLatest(ExportFocusListsManagerReport, onExportFocusListsManagerReport),
    takeLatest(ExportFocusListsManagerReportSuccess, onExportFocusListsManagerReportSuccess),
    takeLatest(GetReportingFocusListsManagerReport, onGetReportingFocusListsManagerReport),
    takeLatest(GetFocusListsManagerReportSchema, onGetFocusListsManagerReportSchema),
    takeLatest(CreateFocusListsManagerSavedReport, onCreateFocusListsManagerSavedReport),
    takeLatest(UpdateFocusListsManagerSavedReport, onUpdateFocusListsManagerSavedReport),
    takeLatest(DeleteFocusListsManagerSavedReport, onDeleteFocusListsManagerSavedReport),
  ]);
}
